'use client';

import { useMemo } from 'react';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { Dropdown } from '@/components/Dropdown';
import { MobileFilterFooter } from '@/components/Filters/MobileFilterFooter';
import { MobileFilterHeader } from '@/components/Filters/MobileFilterHeader';
import { SearchFilterTrigger } from '@/components/Filters/SearchFilterTrigger';
import { SizeBox } from '@/components/Filters/SizeFilter/SizeBox';
import { SizeFilterRow } from '@/components/Filters/SizeFilter/SizeFilterRow';
import baseFilterStyles from '@/components/Filters/baseFilterStyles.module.css';
import { getSizeLocaleName } from '@/components/SizeQuantity/helpers';
import { type SizeFiltersResponse } from '@/modules/filters/aliases';
import { filterSizesByCountry } from '@/modules/filters/filterSizesByCountry';
import { pruneEmptySizes } from '@/modules/filters/pruneEmptySizes';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { toggleInArray } from '@/utils/toggleItemInArray';

type YMALSizeFilterProps = {
  sizes?: string[];
  sizeAggregates: Record<string, number>;
  onSizeFilterChange: (
    sizesOrUpdater: string[] | ((prev: string[] | undefined) => string[])
  ) => void;
  onClearFilters: () => void;
  closeMobileFilterDrawer: () => void;
  isFilterableYMALExperiment?: boolean;
  filteredSizeTree: SizeFiltersResponse;
};

export function YMALSizeFilter({
  sizes,
  sizeAggregates,
  onSizeFilterChange,
  onClearFilters,
  closeMobileFilterDrawer,
  isFilterableYMALExperiment,
  filteredSizeTree,
}: YMALSizeFilterProps) {
  const t = useTranslations('common');
  const title = t('Size');

  const { location } = useCurrentLocation();

  // sizes now contains composite IDs directly
  const selectedCompositeIds = sizes;

  // Filter and prune the size tree that was already filtered by product group
  const sizeTree = useMemo(() => {
    // If no filtered tree provided (still loading or no matching group), return empty
    if (!filteredSizeTree || filteredSizeTree.length === 0) {
      return [];
    }

    // If no aggregates, return empty to avoid showing all sizes
    if (Object.keys(sizeAggregates).length === 0) {
      return [];
    }

    // Prune based on available sizes
    const prunedSizeTree = pruneEmptySizes(filteredSizeTree, sizeAggregates);

    // Get the user's size country (e.g., "GB", "US", "IT")
    const sizeCountry = getSizeLocaleName(location);

    // Then filter by country to show only relevant sizes
    return filterSizesByCountry(prunedSizeTree, sizeCountry);
  }, [filteredSizeTree, sizeAggregates, location]);

  // Disable if no filtered tree or empty tree (loading or no matching group)
  const isSizeFilterDisabled =
    !filteredSizeTree || filteredSizeTree.length === 0;

  function handleSizeBoxClick(compositeId: string) {
    // Toggle the composite ID directly in the filter
    onSizeFilterChange((prev) => toggleInArray(prev, compositeId));
  }

  if (isSizeFilterDisabled) {
    return null;
  }

  return (
    <Dropdown
      TriggerComponent={(props) => (
        <SearchFilterTrigger
          {...props}
          isActive={Boolean(sizes?.length)}
          title={title}
          disabled={isSizeFilterDisabled}
        />
      )}
    >
      {({ className, toggleDropdown }) => (
        <div
          className={clsx(
            className,
            baseFilterStyles.searchFilterDropdownContents
          )}
        >
          <MobileFilterHeader
            title={title}
            toggleDropdown={toggleDropdown}
            closeMobileFilterDrawer={closeMobileFilterDrawer}
          />
          <div className={baseFilterStyles.content}>
            {sizeTree.map(({ id, name, children }) => {
              return (
                <SizeFilterRow
                  key={`${name}-${id}`}
                  name={name}
                  // eslint-disable-next-line react/no-children-prop
                  children={children}
                  Leaf={({ name, value }: { name: string; value: string }) => (
                    <SizeBox
                      name={name}
                      value={value}
                      isSelected={Boolean(
                        selectedCompositeIds?.includes(value)
                      )}
                      handleClick={handleSizeBoxClick}
                    />
                  )}
                />
              );
            })}
          </div>
          <MobileFilterFooter
            handleClear={onClearFilters}
            handleSubmit={closeMobileFilterDrawer}
            hideResultsCount={true}
            isFilterableYMALExperiment={isFilterableYMALExperiment}
          />
        </div>
      )}
    </Dropdown>
  );
}
